import { Gift, Link2, ShoppingBag, UserPlus, type LucideIcon } from 'lucide-react';
import { AnimatedIcon, type IconMotion } from '@/shared/components/animated-icon';
import { Card } from '@/shared/ui/card';
import { Skeleton } from '@/shared/ui/skeleton';
import { cn } from '@/shared/lib/cn';
import { formatRate } from '../model/referral-status';
import type { MgmSummary } from '../model/types';
import { filledCells } from '../model/funnel-grid';

type StepKey = MgmSummary['funnel'][number]['key'];

/**
 * Classes literais (o Tailwind só gera o que aparece escrito no código): cor
 * cheia, a célula "parcial" mais clara e a versão `lg:` de cada uma — o grid
 * tem 30 quadrados no celular e 50 a partir do `lg`.
 */
const STEPS: Record<
  StepKey,
  { label: string; icon: LucideIcon; motion: IconMotion; text: string; on: string; half: string; onLg: string; halfLg: string }
> = {
  indicated: {
    label: 'Indicações',
    icon: Link2,
    motion: 'pop',
    text: 'text-sky-600 dark:text-sky-400',
    on: 'bg-sky-500',
    half: 'bg-sky-500/45',
    onLg: 'lg:bg-sky-500',
    halfLg: 'lg:bg-sky-500/45',
  },
  registered: {
    label: 'Cadastros',
    icon: UserPlus,
    motion: 'bounce',
    text: 'text-violet-600 dark:text-violet-400',
    on: 'bg-violet-500',
    half: 'bg-violet-500/45',
    onLg: 'lg:bg-violet-500',
    halfLg: 'lg:bg-violet-500/45',
  },
  converted: {
    label: 'Primeiras compras',
    icon: ShoppingBag,
    motion: 'bounce',
    text: 'text-emerald-600 dark:text-emerald-400',
    on: 'bg-emerald-500',
    half: 'bg-emerald-500/45',
    onLg: 'lg:bg-emerald-500',
    halfLg: 'lg:bg-emerald-500/45',
  },
  rewarded: {
    label: 'Recompensa completa',
    icon: Gift,
    motion: 'wiggle',
    text: 'text-amber-600 dark:text-amber-400',
    on: 'bg-amber-500',
    half: 'bg-amber-500/45',
    onLg: 'lg:bg-amber-500',
    halfLg: 'lg:bg-amber-500/45',
  },
};

const CELLS_SM = 30;
const CELLS_LG = 50;
const EMPTY = 'bg-foreground/[0.07]';
const EMPTY_LG = 'lg:bg-foreground/[0.07]';

function Row({ step, top, previous, index }: { step: MgmSummary['funnel'][number]; top: number; previous: number | null; index: number }) {
  const meta = STEPS[step.key];
  const sm = filledCells(step.value, top, CELLS_SM);
  const lg = filledCells(step.value, top, CELLS_LG);
  // A última etapa não é subconjunto da anterior quando alguém ganha no cadastro:
  // mostra a fatia do topo; as demais, a passagem da etapa anterior.
  const base = step.key === 'rewarded' ? top : previous;
  const rate = base ? step.value / base : null;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3">
        <span className={cn('flex min-w-0 items-center gap-2 text-sm font-medium', meta.text)}>
          <AnimatedIcon icon={meta.icon} motion={meta.motion} loop delay={index * 0.3} className="size-4" />
          <span className="truncate text-foreground">{meta.label}</span>
        </span>
        <span className="flex shrink-0 items-baseline gap-2">
          <span className="text-base font-semibold tabular-nums">{step.value.toLocaleString('pt-BR')}</span>
          {index > 0 ? (
            <span className="w-12 text-right text-xs tabular-nums text-muted-foreground">{formatRate(rate)}</span>
          ) : null}
        </span>
      </div>
      <div
        className="grid grid-cols-[repeat(30,minmax(0,1fr))] gap-[3px] lg:grid-cols-[repeat(50,minmax(0,1fr))]"
        role="img"
        aria-label={`${meta.label}: ${step.value}`}
      >
        {/* Animação por CSS (tailwindcss-animate), não por JS: são 200 células na tela. */}
        {Array.from({ length: CELLS_LG }).map((_, i) => (
          <span
            key={i}
            className={cn(
              'aspect-square rounded-[3px] animate-in fade-in-0 zoom-in-50 fill-mode-both duration-300',
              i >= CELLS_SM && 'hidden lg:block',
              i < sm.full ? meta.on : i < sm.full + sm.partial ? meta.half : EMPTY,
              i < lg.full ? meta.onLg : i < lg.full + lg.partial ? meta.halfLg : EMPTY_LG,
            )}
            style={{ animationDelay: `${index * 120 + i * 8}ms` }}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * Funil da spec (seção 16) em quadradinhos, no estilo do gráfico de
 * contribuições do GitHub: indicações → cadastros → primeiras compras →
 * recompensa completa. Cada linha pinta a fatia proporcional ao topo.
 */
export function MgmFunnel({ funnel, loading }: { funnel?: MgmSummary['funnel']; loading?: boolean }) {
  const top = funnel?.[0]?.value ?? 0;
  return (
    <Card className="p-4 sm:p-5">
      <p className="text-sm font-medium">Funil do Indique e Ganhe</p>
      <p className="text-xs text-muted-foreground">Da indicação até a recompensa concedida.</p>
      <div className="mt-4 space-y-4">
        {loading || !funnel
          ? Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-10 rounded-xl" />)
          : funnel.map((step, i) => (
              <Row key={step.key} step={step} top={top} previous={i > 0 ? funnel[i - 1]?.value ?? 0 : null} index={i} />
            ))}
      </div>
    </Card>
  );
}
